// @ts-nocheck
import React from 'react'

import { Anchor } from './common/Anchor/Anchor'
import { Note } from './common/Note/Note'

import { useFloatingElement } from 'hooks'
import { TooltipHandlersContext, TooltipPropsContext } from './context'

export const Tooltip = ({
  children,
  delay = 100,
  initialPlacement = 'top',
  hasArrow = false,
  isInitiallyToggled = false,
}) => {
  const [isToggled, setIsToggled] = React.useState(isInitiallyToggled)

  const isTouchEvent = React.useRef(false)
  const timeout = React.useRef()

  const {
    arrowRef,
    arrowStyle,
    floating,
    floatingStyle,
    middlewareData,
    reference,
  } = useFloatingElement({ initialPlacement, offsetSize: 9.5, hasArrow })

  const handleClose = React.useCallback(() => {
    window.clearTimeout(timeout.current)

    timeout.current = window.setTimeout(() => {
      setIsToggled(false)
    }, delay)
  }, [delay, timeout, setIsToggled])

  const handleOpen = React.useCallback(() => {
    window.clearTimeout(timeout.current)

    timeout.current = window.setTimeout(() => {
      setIsToggled(true)
    }, delay)
  }, [delay, timeout, setIsToggled])

  const handleBlur = React.useCallback(() => {
    if (isTouchEvent.current) return

    handleClose()
  }, [handleClose, isTouchEvent])

  const handleFocus = React.useCallback(() => {
    if (isTouchEvent.current) return

    handleOpen()
  }, [handleOpen, isTouchEvent])

  const handleMouseEnter = React.useCallback(() => {
    if (isTouchEvent.current) return

    handleOpen()
  }, [handleOpen, isTouchEvent])

  const handleMouseLeave = React.useCallback(() => {
    if (isTouchEvent.current) return

    handleClose()
  }, [handleClose, isTouchEvent])

  const handleTouchStart = React.useCallback(
    event => {
      if (event.type === 'touchstart') {
        isTouchEvent.current = true
        document.body.style.WebkitUserSelect = 'none'
      }

      handleOpen()
    },
    [handleOpen],
  )

  const handleTouchEnd = React.useCallback(handleClose, [handleClose])

  const propsContext = React.useMemo(
    () => ({
      arrowRef,
      arrowStyle,
      floating,
      floatingStyle,
      middlewareData,
      reference,
      hasArrow,
      isToggled,
    }),
    [
      arrowRef,
      arrowStyle,
      floating,
      floatingStyle,
      middlewareData,
      reference,
      hasArrow,
      isToggled,
    ],
  )

  const handlersContext = React.useMemo(
    () => ({
      onBlur: handleBlur,
      onFocus: handleFocus,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
    }),
    [
      handleBlur,
      handleFocus,
      handleMouseEnter,
      handleMouseLeave,
      handleTouchStart,
      handleTouchEnd,
    ],
  )

  React.useEffect(() => {
    const handleEscapeKey = event => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        handleMouseLeave()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [handleMouseLeave])

  React.useEffect(() => {
    return () => {
      window.clearTimeout(timeout.current)
    }
  }, [timeout])

  return (
    <TooltipPropsContext.Provider value={propsContext}>
      <TooltipHandlersContext.Provider value={handlersContext}>
        {children}
      </TooltipHandlersContext.Provider>
    </TooltipPropsContext.Provider>
  )
}

Tooltip.Anchor = Anchor
Tooltip.Note = Note
